import { useCallback } from "react";

import { isPosthogBrowserAnalyticsEnabled } from "@/features/analytics/posthogBrowserEnabled";
import { useSampleAccountSession } from "@/features/sampleAccount/useSampleAccountSession";
import { clearAsrClientContextStorage } from "@/lib/asrClientContextStorage";
import { SAMPLE_GUEST_DISPLAY_NAME } from "@shared/sampleAccount";

import { useAuth as useClerkAuth, useUser } from "@clerk/react";
import { usePostHog } from "posthog-js/react";

export function useAppAuth() {
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();
  const { orgId, signOut: clerkSignOut, userId, isLoaded: authLoaded } = useClerkAuth();
  const posthog = usePostHog();
  const { sampleSession, isSampleGuest, endSampleSession } = useSampleAccountSession();

  const signOut = useCallback(
    async (...args: Parameters<typeof clerkSignOut>) => {
      clearAsrClientContextStorage();
      endSampleSession();
      if (isPosthogBrowserAnalyticsEnabled && import.meta.env.VITE_PUBLIC_POSTHOG_KEY && posthog) {
        posthog.reset();
      }
      if (isSampleGuest && !userId) {
        return;
      }
      return clerkSignOut(...args);
    },
    [clerkSignOut, endSampleSession, isSampleGuest, posthog, userId],
  );

  // ASA-1594: sample guests have no Clerk user — expose a stable display name for chrome.
  const displayName = isSampleGuest
    ? SAMPLE_GUEST_DISPLAY_NAME
    : (user?.fullName ?? undefined);
  const displayEmail = isSampleGuest
    ? "sample-guest@asaferrisk.example"
    : (user?.primaryEmailAddress?.emailAddress ?? undefined);

  return {
    user,
    displayName,
    displayEmail,
    signOut,
    orgId: orgId ?? sampleSession?.orgId ?? undefined,
    // Pending sessions (e.g. post sign-up before email verification) expose userId
    // while isSignedIn may still be false — allow onboarding to proceed.
    // ASA-1594: sample guests are authenticated without a Clerk session.
    authenticated: Boolean(isSignedIn || userId || isSampleGuest),
    isOnboarded: Boolean(user?.publicMetadata?.onboarded as boolean | undefined) || isSampleGuest,
    clerkLoaded: Boolean(userLoaded && authLoaded),
    isSampleGuest,
    sampleSession,
  } as const;
}

export default useAppAuth;
